import {
  AuthAccountOrganizationJobPositionStatusChoices,
  calculateAdRemainingDays,
  DangerIcon,
  Typography,
} from "@cpj-ai/panel-npm-package";
import { AdminJobPositionNode } from "@graphql/generated";
import { Chip, Divider } from "@heroui/react";
import { colors } from "@theme/colors";
import React from "react";
import { Link } from "react-router-dom";

export interface JobPositionCardProps {
  jobPosition?: AdminJobPositionNode | null;
  onPress?: (id: string) => void;
}

export const NotRegisteredJobPositionCard: React.FC<JobPositionCardProps> = ({ jobPosition, onPress }) => {
  const isClickable = !!onPress;

  const id = jobPosition?.id;
  const title = jobPosition?.title;
  const skills = jobPosition?.skills;
  const status = jobPosition?.status;

  const validityDate = jobPosition?.validityDate;
  const remainingValidityDays = validityDate
    ? calculateAdRemainingDays(validityDate) > 0
      ? `${calculateAdRemainingDays(validityDate)} day`
      : "Expired"
    : "Not set";

  const renderCardBottom = () => {
    if (status === AuthAccountOrganizationJobPositionStatusChoices.REJECTED) {
      return (
        <div className="flex items-center gap-2 text-xs">
          <DangerIcon className="w-5 h-5" viewBox="0 0 24 24" fill="#D24B56" />
          <p className="font-medium text-primary-600">Job Post is Rejected due to non-compliance with the rules</p>{" "}
          <Link to="/" className="text-primary-500">
            (View reason for rejection)
          </Link>
        </div>
      );
    } else if (status === AuthAccountOrganizationJobPositionStatusChoices.SUBMITTED) {
      return (
        <div className="flex items-center gap-2 text-xs">
          <DangerIcon className="w-5 h-5" viewBox="0 0 24 24" fill="#C4841D" />
          <p className="font-medium text-yellow-600">
            Job Post is Registered and awaiting approval in the system for publication.
          </p>
        </div>
      );
    }
    return null;
  };

  const renderStatusBadge = () => {
    if (status === AuthAccountOrganizationJobPositionStatusChoices.REJECTED) {
      return (
        <div className="border-2 border-primary-400 rounded-lg p-1 flex items-center gap-2.5">
          <DangerIcon className="w-5 h-5" viewBox="0 0 24 24" fill={colors.primary["400"]} />
          <p className="text-primary-400 text-xs font-bold">Rejection and non-publication</p>
        </div>
      );
    } else if (status === AuthAccountOrganizationJobPositionStatusChoices.SUBMITTED) {
      return (
        <div className="border-2 border-yellow-600 rounded-lg p-1 flex items-center gap-2.5">
          <DangerIcon className="w-5 h-5" viewBox="0 0 24 24" fill={colors.yellow["500"]} />
          <p className="text-yellow-600 text-xs font-bold">Waiting for confirmation</p>
        </div>
      );
    }
    return null;
  };

  const borderColor =
    status === AuthAccountOrganizationJobPositionStatusChoices.REJECTED ? "border-primary-400" : "border-yellow-600";
  return (
    <div className={`border  rounded-xl p-5 shadow-sm ${borderColor}`}>
      <div
        className={`flex flex-col gap-3 ${isClickable && "cursor-pointer"}`}
        onClick={() => {
          if (isClickable && id) onPress(id);
        }}
      >
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center gap-2">
            <Typography className="font-semibold">{title ?? "-"}</Typography>
            <div className="border-1 p-1 border-pink-600 rounded-lg">
              <p className="text-xs text-pink-600">New</p>
            </div>
            {renderStatusBadge()}
          </div>
          {validityDate && (
            <div className="flex flex-row items-center gap-2">
              <Typography variant="muted">Ad validity date: {validityDate}</Typography>
              <Chip size="sm">{remainingValidityDays}</Chip>
            </div>
          )}
        </div>
        <Typography>
          Skills:{" "}
          {skills?.length ? (
            <span className="text-xs text-zinc-600">{skills.map((item) => item?.title).join(", ")} </span>
          ) : (
            "-"
          )}
        </Typography>
        <Divider />
        <div className="flex items-center gap-2 text-xs">{renderCardBottom()}</div>
      </div>
    </div>
  );
};
